/**
 * The remote-work card kinds on the case (#205/#206): `go_live` ("I'm on it now"), `end_live` (the next Desk status
 * when the work ends, with its three outcome buttons), `log_time` from a remote session (prompt_ref `assist:<id>`)
 * and the attended/unattended session start cards. Each is frozen: its impact text is shown, then Confirm or Dismiss
 * runs it once.
 */
import { Button, Chip, ConfirmCard, type ConfirmCardProps, DismissControl } from '@patchbay/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import * as React from 'react'
import { previewEntries } from '../board/preview'
import { type EndOutcome, errorText, liveApi, type RemoteWorkCard, remoteWorkKeys } from './api'

export const REMOTE_WORK_KINDS = [
  'go_live',
  'end_live',
  'log_time',
  'start_remote_session',
  'start_remote_support',
  'start_unattended_session',
] as const

const KIND_TITLE: Record<string, string> = {
  go_live: "I'm on it now",
  end_live: 'Work ended: next status?',
  log_time: "Log the remote session's time",
  start_remote_session: 'Start a remote session',
  start_remote_support: 'Start remote support',
  start_unattended_session: 'Connect to an unattended device',
}

/** The remote-work cards that belong on this case's strip (a `log_time` only when a remote session proposed it). */
export function isRemoteWorkCard(card: RemoteWorkCard, caseId: string): boolean {
  if (card.state !== 'proposed' || card.case_id !== caseId) return false
  if (card.kind === 'log_time') {
    // A remote session's time (assist:) or a Work it now timer's (live:, #1014) left for later.
    const ref = String(card.payload?.prompt_ref ?? '')
    return ref.startsWith('assist:') || ref.startsWith('live:')
  }
  return (
    card.kind === 'go_live' ||
    card.kind === 'end_live' ||
    card.kind === 'start_remote_session' ||
    card.kind === 'start_remote_support' ||
    card.kind === 'start_unattended_session'
  )
}

/** The frozen preview as label/value rows. Goes through the shared formatter: a `rows` array of `{label, value}`
 * objects must never reach `String()` (#998 showed "rows | [object Object],[object Object]"). */
export function frozenPreviewRows(preview: Record<string, unknown>): { label: string; value: string }[] {
  return previewEntries(preview).filter((row) => row.value.trim())
}

function FrozenPreview({ preview }: { preview: Record<string, unknown> }) {
  const rows = frozenPreviewRows(preview)
  if (!rows.length) return null
  return (
    <dl className="mb-2 grid gap-1 rounded-md bg-muted/60 px-3 py-2 text-xs" data-slot="frozen-preview">
      {rows.map((row, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional and a label can repeat
        <div key={`${row.label}-${i}`} className="grid grid-cols-[7rem_1fr] gap-2">
          <dt className="text-muted-foreground">{row.label}</dt>
          <dd className="whitespace-pre-wrap break-words">{row.value}</dd>
        </div>
      ))}
    </dl>
  )
}

function impactOf(card: RemoteWorkCard): ConfirmCardProps['impact'] {
  const audience = card.audience === 'unclassified' ? 'internal' : card.audience
  return { audience, text: card.impact_text }
}

const OUTCOMES: EndOutcome[] = ['waiting', 'resolved', 'keep']

/** "Waiting: Waiting on Customer" / "Resolved: Resolved - Awaiting Confirmation" / "Keep In Progress". */
export function outcomeLabel(outcome: EndOutcome, status: string | null | undefined): string {
  if (outcome === 'keep') return status ? `Keep ${status}` : 'Keep the current status'
  const head = outcome === 'waiting' ? 'Waiting' : 'Resolved'
  return status ? `${head}: ${status}` : head
}

function choicesOf(card: RemoteWorkCard): Partial<Record<EndOutcome, string | null>> {
  const c = card.payload?.choices
  return c && typeof c === 'object' ? (c as Partial<Record<EndOutcome, string | null>>) : {}
}

/**
 * One remote-work card, decided in place. `pickOutcome` (default on) gives an `end_live` card its three outcome
 * buttons; a different outcome than the frozen one confirms with `edited_payload: {outcome}` (the server merges it).
 */
export function RemoteWorkCardView({
  card: initial,
  pickOutcome = true,
  onDecided,
  children,
}: {
  card: RemoteWorkCard
  pickOutcome?: boolean
  onDecided?: (card: RemoteWorkCard) => void
  /** Rendered under the card once it is confirmed (e.g. the join link of a started session). */
  children?: (card: RemoteWorkCard) => React.ReactNode
}) {
  const qc = useQueryClient()
  const [card, setCard] = React.useState(initial)
  const [error, setError] = React.useState<string | null>(null)
  const decide = useMutation({
    mutationFn: (input: { kind: 'confirm'; edited?: Record<string, unknown> | null } | { kind: 'dismiss'; reason?: string | null }) =>
      input.kind === 'confirm' ? liveApi.confirm(card.id, input.edited) : liveApi.dismiss(card.id, input.reason ?? null),
    onMutate: () => setError(null),
    onSuccess: (next) => {
      setCard(next)
      onDecided?.(next)
      void qc.invalidateQueries({ queryKey: remoteWorkKeys.all })
    },
    onError: (e) => setError(errorText(e)),
  })
  const status: ConfirmCardProps['status'] = decide.isPending
    ? 'running'
    : card.state === 'confirmed' || card.state === 'edited_confirmed'
      ? 'confirmed'
      : card.state === 'dismissed' || card.state === 'expired'
        ? 'dismissed'
        : card.state === 'failed'
          ? 'failed'
          : 'pending'
  const title = card.title || KIND_TITLE[card.kind] || card.kind
  const done = status === 'confirmed'
  const errorLine = error ? (
    <p role="alert" className="mt-2 text-sm text-destructive">
      Not done: {error}
    </p>
  ) : null

  if (!card.can_confirm && status === 'pending') {
    return (
      <p className="text-sm text-muted-foreground" data-card-kind={card.kind}>
        {title}: waiting for someone whose role can confirm it.
      </p>
    )
  }

  if (card.kind === 'end_live' && pickOutcome && status === 'pending') {
    const choices = choicesOf(card)
    const frozen = (card.payload?.outcome as EndOutcome | undefined) ?? 'waiting'
    return (
      <section
        data-card-kind="end_live"
        aria-label="Confirm card: end_live"
        className="rounded-lg border border-primary/40 bg-card p-3.5 shadow-xs"
      >
        <div className="mb-1 flex flex-wrap items-center gap-1.5">
          <Chip tone="outline" className="font-mono text-2xs">
            end_live
          </Chip>
          <span className="text-2xs text-subtle">runs in Patchbay</span>
        </div>
        <h3 className="text-base font-semibold">{title}</h3>
        <p className="mt-2 text-sm text-foreground/85" data-slot="impact">
          {card.impact_text}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {OUTCOMES.map((o) => (
            <Button
              key={o}
              variant={o === frozen ? 'primary' : 'outline'}
              disabled={decide.isPending}
              onClick={() => decide.mutate({ kind: 'confirm', edited: o === frozen ? null : { outcome: o } })}
            >
              {outcomeLabel(o, choices[o])}
            </Button>
          ))}
          <DismissControl pending={decide.isPending} onDismiss={(reason) => decide.mutate({ kind: 'dismiss', reason })} />
        </div>
        {errorLine}
      </section>
    )
  }

  return (
    <div>
      <FrozenPreview preview={card.preview ?? {}} />
      <ConfirmCard
        kind={card.kind}
        title={title}
        impact={impactOf(card)}
        executor={card.executor}
        status={status}
        onConfirm={() => decide.mutate({ kind: 'confirm', edited: null })}
        onDismiss={(reason) => decide.mutate({ kind: 'dismiss', reason })}
      />
      {errorLine}
      {done && children ? children(card) : null}
    </div>
  )
}
